import { FormikForm } from '@/components/form/FormikForm/FormikForm';
import { useMutation } from '@tanstack/react-query';
import { useFormik } from 'formik';
import { Button } from '@/components/Button/Button';
import { OrganizationEditType } from '../../types/types';
import { amplitudeService } from '@/services/amplitudeService/amplitudeService';
import { AMPLITUDE_EVENTS } from '@/services/amplitudeService/amplitudeEvents';
import {
  EDIT_ORGANIZATION_VALIDATION_SCHEMA,
  getEditOrganizationInitialValues,
  getIsAllowedToChangePlan,
} from './const';
import { getResponseError } from '@/helpers/apiHelpers/responseHelpers';
import { FormikInput } from '@/components/form/fields/FormikInput/FormikInput';
import { FormikSelect } from '@/components/form/fields/FormikSelect/FormikSelect';
import { FormikSwitchToggle } from '@/components/form/fields/FormikSwitchToggle/FormikSwitchToggle';
import { ButtonGroup } from '@/components/Button/ButtonGroup/ButtonGroup';
import { SUBSCRIPTION_PLAN_TYPE_OPTIONS } from '../CreateOrganizationForm/const';
import { getChangedFormValues } from '@/helpers/formHelpers/formHelpers';
import { SubscriptionPlanType } from '@/const/shared';
import { OpcoOrganizationDetailsType } from '@/api/v1/opco/organizations/getOrganizationApi';
import { updateOrganization } from '@/bundle/_Opco/api/api';

type EditOrganizationFormType = {
  organization: OpcoOrganizationDetailsType;
  onSuccess: () => void;
  onCancel: () => void;
};

type EditOrganizationFormValuesType = OrganizationEditType & {
  subscription_plan_type: { label: string; value: SubscriptionPlanType };
};

export const EditOrganizationForm = ({ organization, onSuccess, onCancel }: EditOrganizationFormType) => {
  const { mutate, data, isPending } = useMutation({
    mutationKey: ['edit_organization'],
    mutationFn: (payload: OrganizationEditType) => {
      return updateOrganization(organization.id, payload);
    },
    onSuccess(response) {
      if (response.error) return;

      amplitudeService.logEvent(AMPLITUDE_EVENTS.EditOrganizationSuccess);

      onSuccess();
    },
  });

  const initialValues = getEditOrganizationInitialValues(organization);

  const formik = useFormik({
    initialValues,
    enableReinitialize: true,
    validationSchema: EDIT_ORGANIZATION_VALIDATION_SCHEMA,
    onSubmit: (values) => {
      const payload = getChangedFormValues<EditOrganizationFormValuesType>(values, formik.initialValues);

      if (!payload) {
        onCancel();

        return;
      }

      const formattedPayload = {
        ...payload,
        ...(payload?.subscription_plan_type && { subscription_plan_type: payload?.subscription_plan_type?.value }),
      };

      mutate(formattedPayload);
    },
  });

  const apiError = data?.error;
  const formError = getResponseError(apiError);
  const isAllowedToChangePlan = getIsAllowedToChangePlan(organization);

  return (
    <FormikForm value={formik} error={formError}>
      <FormikInput name='name' label='Name*' placeholder='Enter Name' apiError={apiError} />
      <FormikSelect
        name='subscription_plan_type'
        label='Plan Type*'
        placeholder='Select Plan Type'
        options={SUBSCRIPTION_PLAN_TYPE_OPTIONS}
        apiError={apiError}
        disabled={!isAllowedToChangePlan}
      />
      <FormikSwitchToggle name='is_kyc_allowed' label='Enable ID Verification' apiError={apiError} />
      <FormikSwitchToggle name='is_api_keys_allowed' label='Enable API management' apiError={apiError} />
      <ButtonGroup marginTop='12px'>
        <Button type='button' variant='secondary' onClick={onCancel}>
          Cancel
        </Button>
        <Button type='submit' isLoading={isPending}>
          Save
        </Button>
      </ButtonGroup>
    </FormikForm>
  );
};
